import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Users, Star, Heart, Filter, ChevronDown, ChevronUp, Search, Info, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

const chimpanzeeExperiences = [
  {
    id: 1,
    title: "The Primates Express",
    image: "https://imgur.com/PK2H77z.jpg",
    location: "Multiple Parks",
    duration: "8 days",
    groupSize: "2-6 people",
    price: 2100,
    rating: 4.8,
    reviews: 38,
    description: "Focus on Uganda's incredible primates including gorillas and chimpanzees.",
    tag: "Comprehensive",
    highlights: [
      "Gorilla trekking",
      "Chimpanzee tracking",
      "Nature walks",
      "Cultural visits"
    ]
  },
  {
    id: 2,
    title: "Great Apes of Uganda Safari",
    image: "https://imgur.com/HE1ci9r.jpg",
    location: "Multiple Parks",
    duration: "7 days",
    groupSize: "2-6 people",
    price: 3200,
    rating: 4.8,
    reviews: 42,
    description: "A specialized tour for primate lovers, tracking mountain gorillas and chimpanzees.",
    tag: "Premium",
    highlights: [
      "Gorilla trekking",
      "Chimpanzee tracking",
      "Golden monkey viewing",
      "Forest walks"
    ]
  },
  {
    id: 3,
    title: "Kibale Forest Chimpanzee Trek",
    image: "https://imgur.com/ePFOven.jpg",
    location: "Kibale Forest",
    duration: "1 day",
    groupSize: "1-6 people",
    price: 200,
    rating: 4.9,
    reviews: 156,
    description: "Experience an intimate encounter with our closest relatives in their natural habitat.",
    tag: "Most Popular",
    highlights: [
      "Chimpanzee tracking",
      "Forest walks",
      "Bird watching",
      "Nature photography"
    ]
  }
];

const ChimpanzeeTracking = () => {
  const [selectedExperience, setSelectedExperience] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [durationFilter, setDurationFilter] = useState<string[]>([]);
  const { t } = useTranslation();

  const filteredExperiences = chimpanzeeExperiences.filter(exp => {
    const matchesSearch = exp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         exp.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = exp.price >= priceRange[0] && exp.price <= priceRange[1];
    const matchesDuration = durationFilter.length === 0 || 
                           durationFilter.some(d => exp.duration.includes(d));
    
    return matchesSearch && matchesPrice && matchesDuration;
  });

  const handleBookNow = (experience: any) => {
    setSelectedExperience(experience);
    setIsBookingModalOpen(true);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/ePFOven.jpg"
            alt="Chimpanzee Tracking"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-trips" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('adventures.chimpanzeeTracking')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('common.chimpanzeeTrackingDescription')}
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">1,500+</div>
                <div className="text-sm text-white/80">{t('common.chimpanzees')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">98%</div>
                <div className="text-sm text-white/80">{t('common.successRate')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">2-5h</div>
                <div className="text-sm text-white/80">{t('common.trekDuration')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">4.9/5</div>
                <div className="text-sm text-white/80">{t('common.experienceRating')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('pages.gorillaTrekking.essentialInfo')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Calendar className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.gorillaTrekking.bestTime')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.juneToSeptember')} ({t('common.drySeason')})</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.decemberToFebruary')} ({t('common.drySeason')})</span>
                </li>
                <li className="flex items-start">
                  <Info className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.yearRoundTracking')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Users className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.gorillaTrekking.requirements')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.minimumAge')}: 12 {t('common.years')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.basicFitnessLevel')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.trackingPermitRequired')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Info className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.gorillaTrekking.whatToBring')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.hikingBootsAndRainGear')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.cameraNoFlash')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.waterAndSnacks')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('common.chimpanzeeTrackingExperiences')}</h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            {filteredExperiences.map((experience) => (
              <div 
                key={experience.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-32 sm:h-48">
                  <img 
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover"
                  />
                  <button className="absolute top-2 right-2 p-1 sm:p-1.5 bg-white/80 rounded-full hover:bg-white transition-colors">
                    <Heart className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600 hover:text-red-500" />
                  </button>
                  {experience.tag && (
                    <div className="absolute top-2 left-2 bg-ta-green text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                      {experience.tag}
                    </div>
                  )}
                </div>

                <div className="p-2 sm:p-4">
                  <div className="flex items-center mb-1 sm:mb-2">
                    <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs sm:text-sm font-medium">{experience.rating}</span>
                    <span className="mx-1 text-gray-400 text-xs">•</span>
                    <span className="text-xs text-gray-600">{experience.reviews} {t('common.reviews')}</span>
                  </div>

                  <h3 className="text-xs sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2">{experience.title}</h3>

                  <div className="space-y-1 mb-2 sm:mb-4">
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {experience.location}
                    </div>
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {experience.duration}
                    </div>
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <Users className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {experience.groupSize}
                    </div>
                  </div>

                  <p className="text-[10px] sm:text-sm text-gray-600 mb-2 sm:mb-4 line-clamp-2">{experience.description}</p>

                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
                    {experience.highlights.map((highlight, index) => (
                      <span 
                        key={index}
                        className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-ta-green/10 text-ta-green rounded-full text-[8px] sm:text-xs"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[8px] sm:text-xs text-gray-500">{t('common.from')}</p>
                      <p className="text-sm sm:text-xl font-bold text-gray-900">${experience.price}</p>
                    </div>
                    <button
                      onClick={() => handleBookNow(experience)}
                      className="px-2 py-1 sm:px-4 sm:py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors text-[10px] sm:text-sm"
                    >
                      {t('common.bookNow')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedExperience && (
        <BookingModal
          experience={selectedExperience}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedExperience(null);
          }}
        />
      )}

      <Footer />
    </>
  );
};

export default ChimpanzeeTracking;